import globalMessage from "@/utils/globalMessage";
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Input, Button, Spin, Modal, Row, Col } from "antd";
import DetailTabs from "@components/DetailTabs";
import { api } from "@redux/api";
import RecordAuditTimeline from "@components/Table/RecordAuditLog";
import { useSelector } from "react-redux";
import moment from "moment";

import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { useAuth, AUTH_LEVEL } from "@/hooks/use-auth";
import { normalizeRole } from "@/hooks/use-role";
import FunctionalFormFieldsEditor, {
  normalizeFieldsEditorValue,
} from "@containers/management/FunctionalForm/FunctionalFormFieldsEditor";
import EquipmentCategoryLookup from "@containers/Lookup/EquipmentCategoryLookup";
import CompanyLookup from "@containers/Lookup/CompanyLookup";
import "./index.css";
import Select from "@components/ResponsiveSelect";
import {
  effectiveUiFormType,
  toWireFormPayload,
} from "@/utils/form-type-generic-compat";

const FORMS_LIST_PATH = "/admin/forms";

const FunctionalFormDetail = ({ isAdd = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { closeTab, refreshTab } = useKeepAliveContext();
  const canWrite = useAuth("functionform", AUTH_LEVEL.WRITE);
  const canFullAccess = useAuth("functionform", AUTH_LEVEL.FULL_ACCESS);

  const isCreatePage = isAdd === true;
  const [loading, setLoading] = useState(!isCreatePage);
  const [readOnly, setReadOnly] = useState(!isCreatePage);
  const [detailData, setDetailData] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("1");

  const userName = useSelector((state) =>
    state?.Auth?.user ||
    state?.Auth?.first_name + " " + state?.Auth?.last_name
  );

  // Elevated: role === 1 (system admin) or Django is_superuser
  const authRoleValue = useSelector((state) => state?.Auth?.role);
  const isSuperuserFlag = useSelector((state) => Boolean(state?.Auth?.is_superuser));
  const isElevatedUser = normalizeRole(authRoleValue) === 1 || isSuperuserFlag;

  // Regular user's own company (used to pre-fill company on create)
  const authCompanyList = useSelector((state) => state?.Auth?.company);
  const { userCompanyId, userCompanyName } = useMemo(() => {
    const list = Array.isArray(authCompanyList) ? authCompanyList : [];
    const rec = list[0];
    return {
      userCompanyId: rec?.id != null ? rec.id : null,
      userCompanyName: rec?.name != null ? rec.name : "",
    };
  }, [authCompanyList]);

  // ── Data loading ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isCreatePage && id) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isCreatePage]);

  useEffect(() => {
    if (!isCreatePage) return;
    setLoading(false);
    const initial = {
      form_type: "functional_test",
      fields: { rows: [], columns: [], cells: {} },
    };
    // Auto-assign the user's own company when creating; elevated users pick via CompanyLookup
    if (!isElevatedUser && userCompanyId != null) {
      initial.company = userCompanyId;
    }
    form.setFieldsValue(initial);
  }, [isCreatePage, isElevatedUser, userCompanyId, form]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const response = await api.getFormById(id);
      const data = response.data?.results?.[0];
      if (response.status === 200 && data) {
        setDetailData(data);
        const displayFormType = effectiveUiFormType(data) || data.form_type;
        form.setFieldsValue({
          ...data,
          form_type: displayFormType,
          created_date: data.created_date
            ? moment(data.created_date).format("YYYY-MM-DD HH:mm")
            : "",
          modified_date: data.modified_date
            ? moment(data.modified_date).format("YYYY-MM-DD HH:mm")
            : "",
          fields: normalizeFieldsEditorValue(data.fields),
        });
      } else {
        globalMessage.error({ message: "Error", description: "Form not found" });
      }
    } catch (error) {
      console.error("Error fetching form:", error);
      globalMessage.error({ message: "Error", description: "Failed to load form details" });
    } finally {
      setLoading(false);
    }
  };

  // ── Edit / cancel ─────────────────────────────────────────────────────────

  const handleEdit = () => setReadOnly(false);

  const handleCancel = () => {
    setReadOnly(true);
    if (detailData) {
      const displayFormType = effectiveUiFormType(detailData) || detailData.form_type;
      form.setFieldsValue({
        ...detailData,
        form_type: displayFormType,
        created_date: detailData.created_date
          ? moment(detailData.created_date).format("YYYY-MM-DD HH:mm")
          : "",
        modified_date: detailData.modified_date
          ? moment(detailData.modified_date).format("YYYY-MM-DD HH:mm")
          : "",
        fields: normalizeFieldsEditorValue(detailData.fields),
      });
    }
  };

  const handleCancelCreate = () => {
    closeTab();
    navigate(FORMS_LIST_PATH);
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const buildCreateBody = (values) => {
    const normalizedFields = normalizeFieldsEditorValue(
      values.fields ?? { rows: [], columns: [], cells: {} }
    );
    const wire = toWireFormPayload(values.form_type, normalizedFields);
    const body = {
      name: values.name,
      form_type: wire.form_type,
      fields: wire.fields,
    };
    if (values.description) body.description = values.description;
    if (values.equipment_category != null && values.equipment_category !== "")
      body.equipment_category = values.equipment_category;
    if (values.company != null && values.company !== "")
      body.company = values.company;
    return body;
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      if (isCreatePage) {
        const body = buildCreateBody(values);
        const response = await api.createForm(body);
        if (response.status === 200 || response.status === 201) {
          globalMessage.success({ message: "Success", description: "Form created successfully" });
          refreshTab(FORMS_LIST_PATH);
          closeTab();
          navigate(FORMS_LIST_PATH);
        } else {
          globalMessage.error({ message: "Error", description: "Failed to create form" });
        }
      } else {
        const normalizedFields = normalizeFieldsEditorValue(values.fields);
        const wire = toWireFormPayload(values.form_type, normalizedFields);
        const payload = {
          name: values.name,
          form_type: wire.form_type,
          modifiedby_name: userName,
          fields: wire.fields,
        };
        if (values.description) payload.description = values.description;
        if (values.equipment_category != null && values.equipment_category !== "")
          payload.equipment_category = values.equipment_category;
        if (values.company != null && values.company !== "")
          payload.company = values.company;
        const response = await api.editForm(id, payload);
        if (response.status === 200) {
          globalMessage.success({ message: "Success", description: "Form updated successfully" });
          setReadOnly(true);
          fetchDetail();
          refreshTab(FORMS_LIST_PATH);
        } else {
          globalMessage.error({ message: "Error", description: "Failed to update form" });
        }
      }
    } catch (error) {
      console.error("Error saving form:", error);
      const data = error?.response?.data;
      let desc = isCreatePage ? "Failed to create form" : "Failed to update form";
      if (data != null) {
        if (typeof data === "object" && !Array.isArray(data)) {
          const parts = [];
          Object.keys(data).forEach((k) => {
            const v = data[k];
            if (Array.isArray(v)) parts.push(`${k}: ${v.join(", ")}`);
            else if (v != null) parts.push(`${k}: ${String(v)}`);
          });
          if (parts.length) desc = parts.join("; ");
        } else if (typeof data === "string") {
          desc = data;
        } else if (Array.isArray(data)) {
          desc = data.join("; ");
        }
      }
      globalMessage.error({
        message: "Error",
        description: desc,
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this form?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.deleteForm(id);
          if (response.status === 204 || response.status === 200) {
            globalMessage.success({ message: "Success", description: "Form deleted successfully" });
            refreshTab(FORMS_LIST_PATH);
            closeTab();
            navigate(FORMS_LIST_PATH);
          } else {
            globalMessage.error({ message: "Error", description: "Failed to delete form" });
          }
        } catch (error) {
          console.error("Error deleting form:", error);
          globalMessage.error({ message: "Error", description: "Failed to delete form" });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // ── Clone ─────────────────────────────────────────────────────────────────

  const handleCloneTemplate = () => {
    Modal.confirm({
      title: "Clone this template?",
      content: "A copy will be created as a new form. You can edit it afterwards.",
      okText: "Clone",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.cloneForm(id);
          const raw = response.data;
          const newId =
            raw?.id ??
            raw?.pk ??
            (Array.isArray(raw?.results) ? raw.results[0]?.id : null);
          if ((response.status === 200 || response.status === 201) && newId != null) {
            globalMessage.success({ message: "Success", description: "Template cloned successfully" });
            refreshTab(FORMS_LIST_PATH);
            closeTab();
            navigate(`/admin/forms-detail/${newId}`);
          } else {
            globalMessage.error({
              message: "Error",
              description: "Clone succeeded but no new id was returned",
            });
          }
        } catch (error) {
          console.error("Error cloning form:", error);
          globalMessage.error({ message: "Error", description: "Failed to clone template" });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // ── Toolbar ───────────────────────────────────────────────────────────────

  const renderOperationButton = () => {
    if (isCreatePage) {
      return (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button onClick={handleCancelCreate}>
            <i className="fa-solid fa-xmark pr-2"></i>Cancel
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2"></i>Save
          </Button>
        </div>
      );
    }
    if (readOnly) {
      return (
        <div style={{ display: "flex", gap: "8px" }}>
          {canWrite && (
            <Button type="primary" onClick={handleEdit}>
              <i className="fa-solid fa-pen-to-square pr-2"></i>Edit
            </Button>
          )}
          {canWrite && (
            <Button onClick={handleCloneTemplate}>
              <i className="fa-regular fa-copy pr-2"></i>Clone template
            </Button>
          )}
          {canFullAccess && (
            <Button danger onClick={handleDelete}>
              <i className="fa-solid fa-trash pr-2"></i>Delete
            </Button>
          )}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", gap: "8px" }}>
        <Button type="primary" onClick={() => form.submit()}>
          <i className="fa-solid fa-floppy-disk pr-2"></i>Save
        </Button>
        <Button onClick={handleCancel}>Cancel</Button>
      </div>
    );
  };

  // ── Form fields ───────────────────────────────────────────────────────────

  const renderCompanyField = () => {
    // Read-only mode: always plain text
    if (readOnly && !isCreatePage) {
      return <span>{detailData?.company_name || "System"}</span>;
    }
    // Elevated users: can select any company or System
    if (isElevatedUser) {
      return (
        <CompanyLookup
          allowSystemOption
          value={detailData?.company_name || "System"}
          formRef={form}
        />
      );
    }
    // Regular users: company is auto-assigned — display only
    return (
      <Input
        disabled
        value={isCreatePage ? userCompanyName : detailData?.company_name || ""}
      />
    );
  };

  const renderFormFields = () => (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="horizontal"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      className="detailForm"
                        autoComplete="off">
      <Row gutter={[0, 0]}>
        {!isCreatePage && (
          <>
            <Col xs={24} lg={12}>
              <Form.Item label="ID" name="id">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={0} lg={12} />
          </>
        )}

        <Col xs={24} lg={12}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "This field is required" }]}
          >
            <Input disabled={readOnly && !isCreatePage} allowClear />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item
            label="Form Type"
            name="form_type"
            rules={[{ required: true, message: "This field is required" }]}
          >
            {readOnly && !isCreatePage ? (
              <span>{detailData?.form_type_display || ""}</span>
            ) : (
              <Select style={{ width: "100%" }}>
                <Select.Option value="functional_test">Functional Test</Select.Option>
                <Select.Option value="pm_checklist">PM Checklist</Select.Option>
                <Select.Option value="generic">Generic</Select.Option>
              </Select>
            )}
          </Form.Item>
        </Col>

        <Col xs={24} lg={24}>
          <Form.Item
            className="functional-form-full-row-item"
            label="Description"
            name="description"
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
          >
            <Input.TextArea
              rows={2}
              disabled={readOnly && !isCreatePage}
              allowClear
              placeholder="Optional"
            />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item name="equipment_category" hidden>
            <Input />
          </Form.Item>
          <Form.Item label="Equipment Category">
            {readOnly && !isCreatePage ? (
              <Input disabled value={detailData?.equipment_category_name || ""} />
            ) : (
              <EquipmentCategoryLookup
                value={detailData?.equipment_category_name}
                formRef={form}
              />
            )}
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          {/* Hidden FK — written by CompanyLookup.onSelect or create init */}
          <Form.Item name="company" hidden>
            <Input />
          </Form.Item>
          <Form.Item label="Company">
            {renderCompanyField()}
          </Form.Item>
        </Col>

        <Col xs={24} lg={24}>
          <Form.Item
            className="functional-form-full-row-item"
            label="Fields"
            name="fields"
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
          >
            <FunctionalFormFieldsEditor readOnly={readOnly && !isCreatePage} />
          </Form.Item>
        </Col>

        {!isCreatePage && (
          <>
            <Col xs={24} lg={12}>
              <Form.Item label="Created By">
                <span>{detailData?.createdby_name}</span>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Created Date">
                <span>
                  {detailData?.created_date
                    ? moment(detailData.created_date).format("YYYY-MM-DD HH:mm")
                    : ""}
                </span>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Modified By">
                <span>{detailData?.lastModified_name}</span>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Modified Date">
                <span>
                  {detailData?.modified_date
                    ? moment(detailData.modified_date).format("YYYY-MM-DD HH:mm")
                    : ""}
                </span>
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
    </Form>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading && !detailData && !isCreatePage) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  const tabItems = [
    {
      key: "1",
      label: (
        <span>
          <i className="fa-regular fa-rectangle-list pr-2"></i>Detail
        </span>
      ),
      children: (
        <div>
          <div className="slds-m-around_x-small">
            <div className="functional-form-detail-inner px-4">
              <div className="w-full">{renderFormFields()}</div>
            </div>
          </div>
        </div>
      ),
    },
    ...(!isCreatePage
      ? [
          {
            key: "2",
            label: (
              <span>
                <i className="fa-solid fa-clock-rotate-left pr-2"></i>Audit Log
              </span>
            ),
            children: (
              <div style={{ padding: "8px 0" }}>
                <RecordAuditTimeline moduleId={id} module="Form" />
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="functional-form-detail-container">
      <Spin spinning={loading}>
        <DetailTabs
          defaultActiveKey="1"
          destroyInactiveTabPane={true}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          tabBarExtraContent={activeTabKey === "1" ? renderOperationButton() : null}
          items={tabItems}
        />
      </Spin>
    </div>
  );
};

export default FunctionalFormDetail;
